import config from '../../config';
import { getClientData } from '../clientData';
import { logWarning } from '../log';
import { isAndroid, isIOS } from './deviceDetection';

const IOS_BUNDLE_ID = 'eu.vansite.mobile';
const IOS_LOOKUP_URL = `https://itunes.apple.com/lookup?bundleId=${IOS_BUNDLE_ID}`;
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour cooldown between checks

let lastChecked = null;

const TRANSLATIONS = {
  de: { title: 'Update verfügbar', message: 'Eine neue Version von VanSite ({version}) ist verfügbar.', update: 'Aktualisieren', later: 'Später' },
  en: { title: 'Update Available', message: 'A new version of VanSite ({version}) is available.', update: 'Update', later: 'Later' },
  es: { title: 'Actualización disponible', message: 'Una nueva versión de VanSite ({version}) está disponible.', update: 'Actualizar', later: 'Más tarde' },
  fr: { title: 'Mise à jour disponible', message: 'Une nouvelle version de VanSite ({version}) est disponible.', update: 'Mettre à jour', later: 'Plus tard' },
  it: { title: 'Aggiornamento disponibile', message: 'Una nuova versione di VanSite ({version}) è disponibile.', update: 'Aggiorna', later: 'Più tardi' },
  no: { title: 'Oppdatering tilgjengelig', message: 'En ny versjon av VanSite ({version}) er tilgjengelig.', update: 'Oppdater', later: 'Senere' },
  sv: { title: 'Uppdatering tillgänglig', message: 'En ny version av VanSite ({version}) är tillgänglig.', update: 'Uppdatera', later: 'Senare' },
};

const getLocaleStrings = () => {
  const locale = getClientData('vansite.locale') || navigator.language?.split('-')[0] || 'en';
  return TRANSLATIONS[locale] || TRANSLATIONS.en;
};

/**
 * Compares two semver version strings.
 * @returns {boolean} true if storeVersion is newer than currentVersion
 */
const isNewerVersion = (currentVersion, storeVersion) => {
  if (!currentVersion || !storeVersion) return false;

  const [majCur, minCur, patCur] = currentVersion.split('.').map(Number);
  const [majStore, minStore, patStore] = storeVersion.split('.').map(Number);

  if ([majCur, minCur, patCur, majStore, minStore, patStore].some(Number.isNaN)) return false;

  return (
    majCur < majStore ||
    (majCur === majStore && minCur < minStore) ||
    (majCur === majStore && minCur === minStore && patCur < patStore)
  );
};

/**
 * Android: Uses the Google Play In-App Updates API via cordova-plugin-in-app-updates.
 * Shows a flexible (non-blocking) update banner from the Play Store.
 */
const checkAndroidUpdate = () => {
  const plugin = window.cordova?.plugins?.InAppUpdate;
  if (!plugin) return;

  plugin.getUpdateAvailability(
    (result) => {
      // Plugin returns a status string: "UPDATE_AVAILABLE", "UPDATE_NOT_AVAILABLE",
      // "DEVELOPER_TRIGGERED_UPDATE_IN_PROGRESS", or "UNKNOWN".
      if (result !== 'UPDATE_AVAILABLE') return;

      plugin.updateFlexible(
        () => {
          // Flexible update started — Play Store handles the UI
        },
        (err) => {
          // UPDATE_TYPE_NOT_ALLOWED and UPDATE_NOT_AVAILABLE are expected
          // Play Store responses (e.g. update type not supported for this
          // app, or the store no longer offers the update flow), not bugs.
          // Was a Sentry noise source (VANSITE-FRONTEND-4N2, 87 users).
          const status = typeof err === 'string' ? err : err?.message;
          if (status === 'UPDATE_TYPE_NOT_ALLOWED' || status === 'UPDATE_NOT_AVAILABLE') {
            console.warn('native-app-update-android-flexible', err);
            return;
          }
          // Handled, not critical: the flexible in-app update banner just
          // doesn't show; the user can still update via the Play Store
          // (VANSITE-FRONTEND-4N2).
          logWarning(err, 'native-app-update-android-flexible');
        },
      );
    },
    (err) => {
      // Best-effort check: fails routinely on devices without Play services,
      // sideloaded builds, or offline — environmental, never a bug. Was the
      // single biggest Sentry noise source (VANSITE-FRONTEND-47M, 2.5k users).
      console.warn('native-app-update-android-availability', err);
    },
  );
};

/**
 * iOS: Checks the iTunes Lookup API for a newer version and shows a confirm dialog.
 * Non-blocking — user can dismiss and continue using the app.
 */
const checkIOSUpdate = async () => {
  const currentVersion = window.currentAppVersion;
  if (!currentVersion) return;

  // AbortSignal.timeout() is not available in iOS Safari/WKWebView <17.4.
  // Use a manual AbortController to stay compatible with iOS 15/16 devices.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(IOS_LOOKUP_URL, { signal: controller.signal });
    const data = await response.json();
    const storeVersion = data?.results?.[0]?.version;

    if (!storeVersion || !isNewerVersion(currentVersion, storeVersion)) return;

    const strings = getLocaleStrings();

    navigator.notification.confirm(
      strings.message.replace('{version}', storeVersion),
      (buttonIndex) => {
        if (buttonIndex === 1) {
          window.open(config.iosStoreLink, '_system');
        }
      },
      strings.title,
      [strings.update, strings.later],
    );
  } catch (err) {
    // Same best-effort reasoning as the Android availability check above.
    console.warn('native-app-update-ios', err);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Triggers platform-native update check.
 * - Android: Play Store flexible in-app update
 * - iOS: iTunes lookup + confirm dialog
 */
export const checkNativeAppUpdate = () => {
  const now = Date.now();
  if (lastChecked && now - lastChecked < CHECK_INTERVAL_MS) return;
  lastChecked = now;

  if (isAndroid()) {
    checkAndroidUpdate();
  } else if (isIOS()) {
    checkIOSUpdate();
  }
};
